import * as XLSXStyle from 'xlsx-js-style';
import {
  applyVentasPorHoraColumnLabelsFromApi,
  mapApiVentasPorHoraTotalesToNormIds,
  mergeVentasPorHoraEncabezado,
  mergeVentasPorHoraFootTotals,
  resolveVentasPorHoraColumns,
  rowToVentasPorHoraNorm,
  sumVentasPorHoraTotals,
  withVentasPorHoraPeriodoLine,
} from './ventasPorHoraReportModel';
import { formatPeriodoDescuentosLine } from './descuentosCortesiasReportModel';
import { appendFiltrosSucursalesCajasExcel } from './reporteFiltrosEncabezadoExport';

function appendVentasPorHoraEncabezadoExcel(aoa, encMerged, form) {
  let titleRowIndex = -1;
  const up = (s) => String(s || '').trim().toUpperCase();
  if (
    encMerged.bloqueTexto &&
    String(encMerged.bloqueTexto).trim() &&
    !encMerged.nombreComercial &&
    !encMerged.reporteTitulo
  ) {
    for (const line of String(encMerged.bloqueTexto).split('\n')) {
      const t = line.trim();
      if (t) aoa.push([t]);
    }
    aoa.push([]);
    return { titleRowIndex: -1 };
  }
  if (encMerged.nombreComercial) {
    aoa.push([up(encMerged.nombreComercial)]);
  }
  if (encMerged.domicilioFiscal && String(encMerged.domicilioFiscal).trim()) {
    aoa.push([String(encMerged.domicilioFiscal).trim()]);
  }
  if (encMerged.rfc) {
    aoa.push([up(`RFC: ${encMerged.rfc}`)]);
  }
  if (encMerged.reporteTitulo) {
    titleRowIndex = aoa.length;
    aoa.push([up(encMerged.reporteTitulo)]);
  }
  if (encMerged.periodoTexto) {
    aoa.push([String(encMerged.periodoTexto)]);
  } else {
    aoa.push([formatPeriodoDescuentosLine(form.fechaInicio, form.fechaFin)]);
  }
  appendFiltrosSucursalesCajasExcel(aoa, encMerged, form);
  if (encMerged.turnosTexto) {
    const tt = String(encMerged.turnosTexto).trim();
    aoa.push([tt.toLowerCase().startsWith('de:') ? tt : `De: ${tt}`]);
  } else {
    aoa.push([`De: ${form.turnoInicio ?? '—'} a ${form.turnoFin ?? '—'}`]);
  }
  aoa.push([]);
  return { titleRowIndex };
}

function excelRawValue(spec, value) {
  if (value == null || value === '') return '';
  if (spec.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? '' : n;
  }
  return value;
}

export function buildVentasPorHoraExcelPayload(rawRows, options = {}) {
  const {
    fechaInicio = '',
    fechaFin = '',
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    encabezado: encApi,
    encabezadoFallback,
    sucursalNombre = '',
    reporteTitulo,
    apiTotales = null,
    columnas = null,
  } = options;

  const form = { fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin };
  const encBase = mergeVentasPorHoraEncabezado(encApi, encabezadoFallback);
  const sn = String(sucursalNombre || '').trim();
  if (sn && !String(encBase.nombreComercial || '').toUpperCase().includes(sn.toUpperCase())) {
    encBase.nombreComercial = encBase.nombreComercial
      ? `${encBase.nombreComercial} - ${sn.toUpperCase()}`
      : sn.toUpperCase();
  }
  const encMerged = withVentasPorHoraPeriodoLine(
    reporteTitulo ? { ...encBase, reporteTitulo } : encBase,
    fechaInicio,
    fechaFin
  );

  const aoa = [];
  const { titleRowIndex } = appendVentasPorHoraEncabezadoExcel(aoa, encMerged, form);

  const list = Array.isArray(rawRows) ? rawRows : [];
  const resolvedBase = resolveVentasPorHoraColumns(list.length > 0 ? list[0] : null);
  const resolved = applyVentasPorHoraColumnLabelsFromApi(resolvedBase, columnas);
  const nCol = resolved.length;

  if (titleRowIndex >= 0 && aoa[titleRowIndex]) {
    const t = aoa[titleRowIndex][0];
    aoa[titleRowIndex] = resolved.map((_, i) => (i === 0 ? t : ''));
  }

  const encRows = aoa.length;
  const normRows = list.map((r) => rowToVentasPorHoraNorm(r, resolved));
  const summed = sumVentasPorHoraTotals(normRows, resolved);
  const totals = mergeVentasPorHoraFootTotals(
    summed,
    mapApiVentasPorHoraTotalesToNormIds(apiTotales),
    resolved
  );

  aoa.push(resolved.map((c) => c.label));
  const colHeaderRow = encRows;
  const dataStartRow = encRows + 1;

  for (const r of normRows) {
    aoa.push(resolved.map((c) => excelRawValue(c, r[c.id])));
  }

  const footRow = resolved.map((c) => {
    if (!c.total) return '';
    const n = totals[c.id];
    return n != null && !Number.isNaN(n) ? n : '';
  });
  aoa.push(footRow);
  const totalRow = dataStartRow + normRows.length;

  return { aoa, resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex, nCol };
}

function styleVentasPorHoraSheet(ws, meta) {
  const { resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex, nCol } = meta;
  if (!resolved.length || colHeaderRow < 0) return;

  if (titleRowIndex >= 0 && nCol > 0) {
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({
      s: { r: titleRowIndex, c: 0 },
      e: { r: titleRowIndex, c: Math.max(nCol - 1, 0) },
    });
    for (let c = 0; c < nCol; c++) {
      const addr = XLSXStyle.utils.encode_cell({ r: titleRowIndex, c });
      const cell = ws[addr];
      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          fill: { fgColor: { rgb: 'FFBFDBFE' } },
        };
      }
    }
  }

  const encEnd = encRows - 1;
  for (let r = 0; r <= encEnd; r++) {
    if (r === titleRowIndex) continue;
    if (nCol > 1) {
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: nCol - 1 } });
    }
    const addr = XLSXStyle.utils.encode_cell({ r, c: 0 });
    const cell = ws[addr];
    if (cell) {
      cell.s = {
        ...(cell.s || {}),
        alignment: { horizontal: 'center', wrapText: true },
        font: { ...((cell.s && cell.s.font) || {}), color: { rgb: 'FF0F172A' } },
      };
    }
  }

  for (let c = 0; c < nCol; c++) {
    const align = resolved[c].align === 'right' ? 'right' : 'left';
    const th = XLSXStyle.utils.encode_cell({ r: colHeaderRow, c });
    const tc = ws[th];
    if (tc) {
      tc.s = {
        ...(tc.s || {}),
        font: { bold: true, color: { rgb: 'FFFFFFFF' } },
        alignment: { horizontal: align },
        fill: { fgColor: { rgb: 'FF0F2F59' } },
        border: { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
      };
    }

    for (let r = dataStartRow; r <= totalRow; r++) {
      const addr = XLSXStyle.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;
      const base = {
        ...(cell.s || {}),
        alignment: { horizontal: align },
        font: { color: { rgb: 'FF0F172A' } },
      };
      if (r === totalRow) {
        const isTotal = resolved[c].total;
        cell.s = {
          ...base,
          font: { bold: true, color: { rgb: 'FF000000' } },
          border: isTotal
            ? {
                top: { style: 'double', color: { rgb: 'FF000000' } },
                bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              }
            : { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
        };
      } else {
        cell.s = base;
      }
    }
  }
}

export function downloadVentasPorHoraExcelFile(rawRows, options, fileName, sheetName) {
  const payload = buildVentasPorHoraExcelPayload(rawRows, options);
  const { aoa, resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex, nCol } =
    payload;

  const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
  if (resolved.length) {
    styleVentasPorHoraSheet(ws, {
      resolved,
      encRows,
      colHeaderRow,
      dataStartRow,
      totalRow,
      titleRowIndex,
      nCol,
    });
  }

  const colCount = Math.max(1, aoa.reduce((m, row) => Math.max(m, row.length), 0));
  ws['!cols'] = [
    { wch: 12 },
    { wch: 22 },
    ...Array.from({ length: Math.max(0, colCount - 2) }, () => ({ wch: 14 })),
  ];

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  XLSXStyle.writeFile(wb, fileName);
}
